import { alpha } from "@mui/material/styles";
import type { ThemeOptions } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";

import { buildAdminThemeComponents } from "./adminThemeComponents";

/* eslint-disable no-inline-colors/no-inline-colors -- This file defines the central admin theme palette tokens and must own the literal source values. */

/** Kate's September UI foundations (F-023–F-073 and colour additions).
 * Keep the legacy palette below for surfaces not yet migrated to this contract.
 * The CSS counterpart is checked by adminCrmFoundations.test.ts.
 */
export const adminCrmFoundationTokens = {
  navy: "#1A1238",
  "navy-2": "#241A4A",
  "navy-text": "#CFC8E6",
  "navy-muted": "#8C84AD",
  pink: "#D6156C",
  "pink-hover": "#B8105C",
  "pink-soft": "#FBE7F0",
  bg: "#FFFFFF",
  "bg-2": "#F7F7F9",
  line: "#E6E6EA",
  "line-2": "#EFEFF2",
  ink: "#1C1C24",
  "ink-2": "#5C5C6A",
  "ink-3": "#8F8F9C",
  green: "#1E8A4C",
  "green-bg": "#E6F5EC",
  amber: "#8A5700",
  "amber-bg": "#FFF3D6",
  red: "#C2331F",
  "red-bg": "#FDEAE6",
  blue: "#2856C9",
  "blue-bg": "#E8EEFB",
  overlay: "rgba(26, 18, 56, 0.28)",
  "menu-shadow": "0 10px 30px rgba(26, 18, 56, 0.14)",
  "modal-shadow": "0 20px 60px rgba(26, 18, 56, 0.18)",
  "drawer-shadow": "-12px 0 32px rgba(0, 0, 0, 0.08)",
  "amber-line": "#F0D58A",
  indigo: "#3B3F9E",
  "indigo-bg": "#EEF0FB",
  "indigo-line": "#CDD3F5",
  "pink-light": "#F4A6C4",
  "white-8": "rgba(255, 255, 255, 0.08)",
  "white-12": "rgba(255, 255, 255, 0.12)",
  "white-20": "rgba(255, 255, 255, 0.2)",
  "avatar-0": "#C94F7C",
  "avatar-1": "#6A4FC9",
  "avatar-2": "#C98A2F",
  "avatar-3": "#4F7CC9",
  "avatar-4": "#2F9E7A",
  "avatar-5": "#C95C4F",
  "avatar-6": "#8C8C9C",
  "avatar-7": "#7B5CFF",
  "avatar-8": "#5C9C4F",
  "avatar-9": "#3B8FC9",
} as const;

export const adminCrmFontFamily = '"Inter", system-ui, sans-serif';

const foundation = {
  white: "#FFFFFF",
  purple: "#0C0140",
  purple80: "#3E307C",
  grey: "#787585",
  grey50: "#D9D9D9",
  greyText: "#6B7280",
  greyTextDark: "#5D5A68",
  pink: "#EF326C",
  pinkLight: "#ff639280",
  fail: "#DB3131",
  shadowBeige: "rgba(213, 201, 152, 0.2)",
} as const;

const backgroundTokens = {
  beige: "#FFFCF5",
  peach: "#FDF6ED",
  merino: "#F5EEE2",
  lavender: "#EDEAFA",
} as const;

const graphTokens = {
  green: "#0FB35B",
  teal: "#3CC7BF",
  red: "#F25252",
  orange: "#FBA95F",
  yellow: "#FFD600",
  blue: "#0056FD",
  deepBlue: "#1850a3",
  fushia: "#DB3DCE",
  pink: foundation.pink,
  pinkLight: foundation.pinkLight,
  purple80: foundation.purple80,
} as const;

export const adminAudienceTagColorPalette = {
  blue: "#2563eb",
  green: "#16a34a",
  gold: "#ca8a04",
  rose: "#e11d48",
  slate: "#475569",
} as const;

const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 768,
    lg: 1200,
    xl: 1600,
  },
};

const typography = {
  fontFamily: adminCrmFontFamily,
  h1: {
    fontSize: "2rem",
    fontWeight: 600,
    lineHeight: 1.4375,
    color: foundation.purple,
  },
  h2: {
    fontSize: "1.5rem",
    fontWeight: 400,
    lineHeight: 1.25,
    color: foundation.purple,
  },
  h3: {
    fontSize: "1.125rem",
    fontWeight: 400,
    lineHeight: 1.333,
  },
  h4: {
    fontSize: "1.25rem",
    fontWeight: 600,
    lineHeight: 1.3,
  },
  h5: {
    fontSize: "1.125rem",
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: "1rem",
    fontWeight: 600,
    lineHeight: 1.5,
  },
  subtitle1: {
    fontSize: "1rem",
    fontWeight: 500,
    lineHeight: 1.28,
  },
  subtitle2: {
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: 1.57,
  },
  body1: {
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: 1.25,
  },
  body2: {
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.286,
  },
  caption: {
    fontSize: "0.75rem",
    fontWeight: 400,
    lineHeight: 1.33,
    color: foundation.grey,
  },
  overline: {
    fontSize: "0.75rem",
    fontWeight: 600,
    lineHeight: 1,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
  },
};

const spacing = 4;

export const buildAdminThemeOptions = (
  mode: "light" | "dark",
): ThemeOptions => {
  const isLight = mode === "light";

  const palette = {
    mode,
    primary: {
      main: isLight ? foundation.pink : "#ffe0e9",
      contrastText: foundation.white,
    },
    secondary: {
      main: foundation.purple,
      contrastText: foundation.white,
    },
    error: {
      main: foundation.fail,
      contrastText: foundation.white,
    },
    success: {
      main: graphTokens.green,
      contrastText: foundation.white,
    },
    warning: {
      main: graphTokens.orange,
      contrastText: foundation.purple,
    },
    info: {
      main: graphTokens.blue,
      contrastText: foundation.white,
    },
    divider: isLight ? foundation.grey50 : alpha(foundation.white, 0.12),
    background: {
      default: isLight ? backgroundTokens.beige : "#121212",
      paper: isLight ? foundation.white : "#1e1e1e",
    },
    text: {
      primary: isLight ? foundation.purple : foundation.white,
      secondary: isLight ? foundation.greyText : alpha(foundation.white, 0.72),
    },
  } as const;

  return {
    palette,
    breakpoints,
    typography,
    spacing,
    shape: {
      borderRadius: 12,
    },
    components: buildAdminThemeComponents({
      isLight,
      foundation,
      backgroundTokens,
      palette,
      typographyFontFamily: typography.fontFamily,
    }) as NonNullable<ThemeOptions["components"]>,
    custom: {
      sidebar: {
        width: 280,
        collapsedWidth: 64,
      },
      header: {
        height: 108,
      },
      layout: {
        contentPadding: spacing * 6,
        sectionSpacing: spacing * 4,
      },
    },
  };
};
